import { useMutation, UseMutationOptions, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { displayToastSuccess } from '@/helpers/display-toast';
import { DEFAULT_COLOR_PALETTE } from '@/lib/constants/colors';
import { RateSourceDTO } from '@/modules/common';
import { useApiError } from '@/modules/common/hooks/use-api-error';
import { isFeatureEnabled } from '@/modules/common/hooks/use-feature-flags';
import {
  invalidateSuggestedApproversQueries,
  invalidateTeamMemberRatesOnProjectQueries,
  invalidateTimeTrackerProjectsQueries,
  invalidateTimeTrackingQueries,
} from '@/modules/common/utils/query-invalidation';

import { createAssignment } from '../../api/assignments.api';
import {
  applyOptimisticMemberUpdate,
  buildOptimisticPlannerAssignment,
  cancelAssignmentQueries,
  invalidateAllAssignmentQueries,
  OptimisticContext,
  patchCacheAfterMutation,
  refetchActiveAssignmentQueriesInBackground,
  rollbackAssignmentData,
  snapshotAssignmentData,
} from '../../helpers/assignment-cache-utils';
import {
  AssignmentProjectResponse,
  AssignmentResponse,
  CreateAssignmentRequest,
  CreateAssignmentWithUIRequest,
  PlannerMemberResponse,
} from '../../types/dto';

/**
 * Hook for creating a new assignment with optimistic updates.
 * Immediately shows the new assignment in the UI before the API call completes.
 * Automatically invalidates only the visible date range queries on success.
 *
 * @param options - Optional TanStack Query mutation options
 * @returns Mutation object with mutate, isPending, etc.
 *
 * @example
 * ```typescript
 * const createMutation = useCreateAssignment({
 *   onSuccess: () => logger.debug(TAG, 'Assignment created!')
 * });
 *
 * createMutation.mutate({
 *   payload: {
 *     userId: 1,
 *     projectId: 2,
 *     startDate: '2025-01-01',
 *     endDate: '2025-01-31',
 *     status: AssignmentStatusDTO.CONFIRMED,
 *     // ... other required fields
 *   },
 *   ui: {
 *     projectName: 'My Project',
 *     projectColorHex: '#FF0000',
 *   }
 * });
 * ```
 */
export function useCreateAssignment(
  options?: UseMutationOptions<AssignmentResponse, Error, CreateAssignmentWithUIRequest>
) {
  const queryClient = useQueryClient();
  const { t } = useTranslation('authenticated.planner');
  const { displayToastErrorFromApiWithFallback } = useApiError();

  const { onSuccess, onError, ...restOptions } = options ?? {};

  return useMutation({
    ...restOptions,
    mutationFn: (request: CreateAssignmentWithUIRequest) => {
      const isCustomRate = request.payload.rateSource === RateSourceDTO.CUSTOM;
      const createAssignmentRequest: CreateAssignmentRequest = {
        ...request.payload,
        customRate: isCustomRate ? request.payload.customRate : null,
        customRateCurrencyCode: isCustomRate ? request.payload.customRateCurrencyCode : null,
      };
      return createAssignment(createAssignmentRequest);
    },
    onMutate: async (variables: CreateAssignmentWithUIRequest): Promise<OptimisticContext> => {
      const { payload, ui } = variables;

      // Cancel outgoing refetches to prevent overwriting optimistic update
      await cancelAssignmentQueries(queryClient);

      // Snapshot previous values for rollback
      const previousData = snapshotAssignmentData(queryClient);

      // Generate temporary optimistic assignment with negative ID
      const optimisticAssignment: AssignmentResponse = {
        id: -Date.now(), // Temporary negative ID
        startDate: payload.startDate,
        endDate: payload.endDate,
        status: payload.status,
        doSkipWeekdays: payload.doSkipWeekdays,
        isBillable: payload.isBillable,
        label: payload.label,
        minutesPerDay: payload.minutesPerDay,
        note: payload.note,
        isBackgroundWork: payload.isBackgroundWork,
        probability: payload.probability ?? null,
        rateSource: payload.rateSource,
        customRate: payload.customRate ?? undefined,
        project: {
          id: payload.projectId,
          name: ui?.projectName || '',
          colorHex: ui?.projectColorHex
            ? ui.projectColorHex.replace('#', '')
            : DEFAULT_COLOR_PALETTE.slate.replace('#', ''),
        } as AssignmentProjectResponse,
        syncToken: '',
      };

      const optimisticPlannerAssignment = buildOptimisticPlannerAssignment(optimisticAssignment);

      // Helper to add assignment to a member
      const addAssignmentToMember = (member: PlannerMemberResponse): PlannerMemberResponse => {
        if (member.user.id === payload.userId) {
          return {
            ...member,
            assignments: [...member.assignments, optimisticPlannerAssignment],
          };
        }
        return member;
      };

      // Optimistically update all three cache views (byMember, byDepartment, byProject)
      applyOptimisticMemberUpdate(queryClient, addAssignmentToMember);

      return { ...previousData, tempAssignmentId: optimisticAssignment.id };
    },
    onSuccess: (data, variables, context) => {
      if (isFeatureEnabled('usePlannerCachePatching')) {
        const ctx = context as OptimisticContext;

        // Single atomic cache update: patch temp ID → real ID + recalculate utilization
        patchCacheAfterMutation(queryClient, {
          assignmentId: ctx.tempAssignmentId,
          serverData: data,
          userId: variables.payload.userId,
          newDates: {
            startDate: variables.payload.startDate,
            endDate: variables.payload.endDate,
          },
          customRateCurrencyCode: variables.payload.customRateCurrencyCode ?? undefined,
        });

        // Fire-and-forget background refetch to reconcile activePeriods from backend
        // (client-side cannot compute rate-change-based period splits).
        refetchActiveAssignmentQueriesInBackground(queryClient);
      } else {
        invalidateAllAssignmentQueries(queryClient);
      }

      // Assignment changes can affect rate calculations for future queries
      invalidateTeamMemberRatesOnProjectQueries(queryClient);
      // Assignment changes affect booked hours shown on the time tracking page
      invalidateTimeTrackingQueries(queryClient);
      // Assignment changes can alter the time-off suggested approvers.
      invalidateSuggestedApproversQueries(queryClient);
      // Assignment changes can flip which projects count as "booked" in the
      // Time Tracking modal's project picker.
      invalidateTimeTrackerProjectsQueries(queryClient);

      displayToastSuccess(
        t('toasts.assignmentCreatedSuccess', {
          userName: variables.ui?.userName ?? '',
          projectName: variables.ui?.projectName ?? '',
        })
      );

      onSuccess?.(data, variables, context);
    },
    onError: (error, variables, context) => {
      // Rollback optimistic updates on error
      rollbackAssignmentData(queryClient, context as OptimisticContext);

      displayToastErrorFromApiWithFallback(
        error,
        'authenticated.planner:toasts.createAssignmentError',
        {
          interpolation: {
            userName: variables.ui?.userName ?? '',
            projectName: variables.ui?.projectName ?? '',
          },
        }
      );

      onError?.(error, variables, context);
    },
  });
}
